import { jsx as _jsx } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { InvoiceDisplayStatus, InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { SearchFilterType } from '~/components/TableFilters/types';
import { DateFilterPicker, DateFilterPickerPresets } from './DateFilterPicker';
export const invoiceFilterTextsPath = 'invoice';
export var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["InvoiceStatus"] = "invoiceStatus";
    FilterType["PaymentStatus"] = "paymentStatus";
    FilterType["Date"] = "date";
    FilterType["QboSync"] = "qboSync";
    FilterType["XeroSync"] = "xeroSync";
    return FilterType;
}({});
export function getTableFilterLabels(filterLabels) {
    return {
        ["invoiceStatus"]: filterLabels.invoiceStatus(),
        ["paymentStatus"]: filterLabels.paymentStatus(),
        ["date"]: filterLabels.date(),
        ["qboSync"]: filterLabels.qboSync(),
        ["xeroSync"]: filterLabels.xeroSync()
    };
}
export function getActiveFilterGroups(params) {
    const accountingFilters = compact([
        params.showQboIntegration && params.hasQboFilterOptions && "qboSync",
        params.xeroIntegrationEnabled && params.showXeroIntegration && params.hasXeroFilterOptions && "xeroSync"
    ]);
    return compact([
        {
            label: params.filterGroupLabels.invoice(),
            filters: [
                "invoiceStatus",
                "paymentStatus",
                "date"
            ]
        },
        accountingFilters.length > 0 && {
            label: params.filterGroupLabels.accounting(),
            filters: accountingFilters
        }
    ]);
}
function invoiceDisplayStatusLabels(labels) {
    return {
        [InvoiceDisplayStatus.Issued]: labels.issued(),
        [InvoiceDisplayStatus.Overdue]: labels.overdue(),
        [InvoiceDisplayStatus.PaymentInitiated]: labels.processing(),
        [InvoiceDisplayStatus.Paid]: labels.paid(),
        [InvoiceDisplayStatus.Voided]: labels.voided(),
        [InvoiceDisplayStatus.Refunded]: labels.refunded(),
        [InvoiceDisplayStatus.RefundFailed]: labels.refundFailed(),
        [InvoiceDisplayStatus.PaymentDisputed]: labels.disputed(),
        [InvoiceDisplayStatus.PartiallyRefunded]: labels.partiallyRefunded()
    };
}
function paymentDisplayStatusLabels(labels) {
    return {
        [InvoicePaymentDisplayStatusType.None]: labels.none(),
        [InvoicePaymentDisplayStatusType.Collected]: labels.collected(),
        [InvoicePaymentDisplayStatusType.Paid]: labels.deposited(),
        [InvoicePaymentDisplayStatusType.PaidExternally]: labels.paidExternally(),
        [InvoicePaymentDisplayStatusType.PaymentFailed]: labels.paymentFailed(),
        [InvoicePaymentDisplayStatusType.PaymentMethodNotSet]: labels.paymentMethodNotSet(),
        [InvoicePaymentDisplayStatusType.PaymentMethodUnverified]: labels.paymentMethodNotVerified(),
        [InvoicePaymentDisplayStatusType.Pending]: labels.issued(),
        [InvoicePaymentDisplayStatusType.Processing]: labels.paymentInitiated(),
        [InvoicePaymentDisplayStatusType.Refunded]: labels.refundInitiated(),
        [InvoicePaymentDisplayStatusType.RefundFailed]: labels.refundFailed(),
        [InvoicePaymentDisplayStatusType.RequiresPaymentApproval]: labels.approvalRequired(),
        [InvoicePaymentDisplayStatusType.RequiresPaymentValidation]: labels.validationRequired(),
        [InvoicePaymentDisplayStatusType.StoppedPayment]: labels.stoppedPayment(),
        [InvoicePaymentDisplayStatusType.Disputed]: labels.disputed(),
        [InvoicePaymentDisplayStatusType.RefundRequested]: labels.refundRequested()
    };
}
function datePresetLabels(labels) {
    return {
        [DateFilterPickerPresets.AllTime]: labels.allTime(),
        [DateFilterPickerPresets.Today]: labels.today(),
        [DateFilterPickerPresets.ThisWeek]: labels.thisWeek(),
        [DateFilterPickerPresets.ThisMonth]: labels.thisMonth(),
        [DateFilterPickerPresets.PreviousMonth]: labels.previousMonth(),
        [DateFilterPickerPresets.LastThreeMonths]: labels.lastThreeMonths()
    };
}
function accountingStatusLabels(labels) {
    return {
        [AccountingStatusFilter.All]: labels.all(),
        [AccountingStatusFilter.Synced]: labels.synced(),
        [AccountingStatusFilter.NotSynced]: labels.notSynced()
    };
}
function InvoiceDateFilter(param) {
    let { value, onChange } = param;
    return /*#__PURE__*/ _jsx(DateFilterPicker, {
        value: value,
        onChange: onChange
    });
}
export function getTableFilterConfig(params) {
    return {
        ["invoiceStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: false,
            options: params.invoiceStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    counter: o.counter
                }))
        },
        ["paymentStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: false,
            options: params.paymentStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    counter: o.counter
                }))
        },
        ["date"]: {
            type: SearchFilterType.CustomFilter,
            removable: false,
            component: InvoiceDateFilter,
            initialValue: DateFilterPickerPresets.AllTime
        },
        ["qboSync"]: {
            type: SearchFilterType.RadioFilter,
            removable: false,
            options: params.qboStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    sublabel: "(".concat(o.counter, ")")
                })),
            defaultOption: AccountingStatusFilter.All
        },
        ["xeroSync"]: {
            type: SearchFilterType.RadioFilter,
            removable: false,
            options: params.xeroStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    sublabel: "(".concat(o.counter, ")")
                })),
            defaultOption: AccountingStatusFilter.All
        }
    };
}
export function formatFilterDescription(params) {
    const { filterType, value, texts } = params;
    switch(filterType){
        case "invoiceStatus":
            {
                const statuses = value;
                if (!statuses.length) return undefined;
                const labels = invoiceDisplayStatusLabels(texts.invoiceDisplayStatusLabels);
                return statuses.map((s)=>labels[s]).join(', ');
            }
        case "paymentStatus":
            {
                const statuses = value;
                if (!statuses.length) return undefined;
                const labels = paymentDisplayStatusLabels(texts.paymentDisplayStatusLabels);
                return statuses.map((s)=>labels[s]).join(', ');
            }
        case "date":
            {
                const labels = datePresetLabels(texts.datePresetLabels);
                return labels[value];
            }
        case "qboSync":
        case "xeroSync":
            {
                const labels = accountingStatusLabels(texts.accountingStatusLabels);
                return labels[value];
            }
    }
}
